const CYPRUS_TZ = 'Asia/Nicosia'

type ZonedParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

function getZonedParts(date: Date, timeZone: string = CYPRUS_TZ): ZonedParts {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  const parts = dtf.formatToParts(date)
  const map: Record<string, string> = {}
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i]
    if (p.type !== 'literal') map[p.type] = p.value
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour === '24' ? '0' : map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
  }
}

/**
 * Convert a Cyprus wall-clock date/time to a real UTC Instant.
 * Studio hours in the app are always Cyprus time (Asia/Nicosia), including DST.
 */
export function cyprusWallTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute = 0,
  second = 0
): Date {
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
  const asCyprus = getZonedParts(utcGuess, CYPRUS_TZ)
  const desiredAsUtcMs = Date.UTC(year, month - 1, day, hour, minute, second)
  const actualAsUtcMs = Date.UTC(
    asCyprus.year,
    asCyprus.month - 1,
    asCyprus.day,
    asCyprus.hour,
    asCyprus.minute,
    asCyprus.second
  )
  return new Date(utcGuess.getTime() + (desiredAsUtcMs - actualAsUtcMs))
}

/** Cyprus calendar Y-M-D for a stored slot.date */
export function getCyprusCalendarDate(slotDate: Date | string | number): {
  year: number
  month: number
  day: number
} {
  const d = new Date(slotDate)
  if (Number.isNaN(d.getTime())) {
    return { year: NaN, month: NaN, day: NaN }
  }
  // Midnight UTC date-only values represent the studio calendar day, not a TZ instant.
  // Avoid shifting them a day when converting through Asia/Nicosia.
  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  ) {
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
    }
  }
  const parts = getZonedParts(d, CYPRUS_TZ)
  return { year: parts.year, month: parts.month, day: parts.day }
}

/** DD.MM.YYYY in Cyprus calendar for a slot.date */
export function formatCyprusDDMMYYYY(slotDate: Date | string | number): string {
  const { year, month, day } = getCyprusCalendarDate(slotDate)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return ''
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`
}

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

/** Weekday name for a slot.date using Cyprus calendar day */
export function getCyprusDayName(slotDate: Date | string | number): string {
  const { year, month, day } = getCyprusCalendarDate(slotDate)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return ''
  const weekday = new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).getUTCDay()
  return DAY_NAMES[weekday] ?? ''
}

/** Booking start instant: slot calendar day in Cyprus + start hour (0-23) in Cyprus */
export function getCyprusBookingStart(
  slotDate: Date | string | number,
  startHour: number
): Date | null {
  if (slotDate == null || !Number.isFinite(Number(startHour))) return null
  const d = new Date(slotDate)
  if (Number.isNaN(d.getTime())) return null
  const { year, month, day } = getCyprusCalendarDate(d)
  return cyprusWallTimeToUtc(year, month, day, Number(startHour), 0, 0)
}

/** True when booking start (Cyprus) is more than `hours` ahead of now */
export function isMoreThanHoursBeforeCyprusStart(
  slotDate: Date | string | number,
  startHour: number,
  hours = 24
): boolean {
  const start = getCyprusBookingStart(slotDate, startHour)
  if (!start) return false
  return start.getTime() - Date.now() > hours * 60 * 60 * 1000
}

export { CYPRUS_TZ }
